import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { apiService } from "../../../services/api";
import { Usuario, PerfilPermissao } from '../../../types/admin';
import { Offcanvas, Modal } from 'bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { modalUtils } from '../../../utils/modalUtils';
import { ModalDetalhesUsuario } from './components/ModalDetalhesUsuario';
import { ModalAlterarPerfil } from './components/ModalAlterarPerfil';
import { ModalExcecoesUsuario } from './components/ModalExcecoesUsuario';
import { ModalAdicionarUsuario } from './components/ModalAdicionarUsuario';
import { ModalEditarUsuario } from './components/ModalEditarUsuario';
import { ModalDeletarReativarUsuario } from './components/ModalDeletarReativarUsuario';
import { API_BASE_URL } from "../../../environment";

const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: Usuario) => React.ReactNode;
  sorter?: (a: Usuario, b: Usuario) => number;
  key?: string;
}

export const GestaoUsuarios = () => {
  const dispatch = useDispatch();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState<Usuario[]>([]);
  const [perfis, setPerfis] = useState<PerfilPermissao[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [perfilFilter, setPerfilFilter] = useState<string>('all');

  // Estados para modais
  const [usuarioDetalhes, setUsuarioDetalhes] = useState<Usuario | null>(null);
  const [usuarioAlterarPerfil, setUsuarioAlterarPerfil] = useState<Usuario | null>(null);
  const [usuarioExcecoes, setUsuarioExcecoes] = useState<Usuario | null>(null);
  const [excecoesInitialTab, setExcecoesInitialTab] = useState<'lista' | 'adicionar'>('lista');
  const [showModalAdicionarUsuario, setShowModalAdicionarUsuario] = useState(false);
  const [usuarioEditar, setUsuarioEditar] = useState<Usuario | null>(null);
  const [usuarioDeletarReativar, setUsuarioDeletarReativar] = useState<{usuario: Usuario, action: 'delete' | 'reactivate'} | null>(null);

  // URL base da API (removido /api/v1 para usar apenas o domínio base)
  const BASE_DOMAIN = API_BASE_URL.replace('/api/v1', '');


  // Função para buscar usuários
  const fetchUsuarios = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUsuarios();

      // Adiciona a propriedade key para cada usuário (necessária para o Ant Design Table)
      const usuariosComKey = response.data.map((usuario: Usuario) => ({
        ...usuario,
        key: usuario.id.toString()
      }));

      setUsuarios(usuariosComKey);
      setFilteredUsuarios(usuariosComKey);
      setError(null);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      setError('Erro ao carregar os usuários. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar perfis
  const fetchPerfis = async () => {
    try {
      const response = await apiService.getPerfisPermissao();
      setPerfis(response.data);
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
    }
  };

  // Carregar dados ao montar o componente
  useEffect(() => {
    Promise.all([fetchUsuarios(), fetchPerfis()]);
  }, []);

  // Efeito para filtrar usuários
  useEffect(() => {
    let filtered = usuarios;

    // Filtro por texto de busca
    if (searchText.trim()) {
      const searchTerms = searchText.toLowerCase().trim().split(' ');
      filtered = filtered.filter(usuario => {
        const searchableFields = [
          usuario.nome,
          usuario.sobrenome,
          usuario.email,
          usuario.cargo,
          usuario.perfil_nome,
          usuario.ativo ? 'ativo' : 'inativo'
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });
    }

    // Filtro por perfil
    if (perfilFilter !== 'all') {
      if (perfilFilter === 'sem-perfil') {
        filtered = filtered.filter(usuario => !usuario.id_perfil_base);
      } else {
        filtered = filtered.filter(usuario => usuario.perfil_codigo === perfilFilter);
      }
    }

    setFilteredUsuarios(filtered);
  }, [searchText, usuarios, perfilFilter]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para abrir modal de detalhes
  const handleAbrirDetalhes = (usuario: Usuario) => {
    setUsuarioDetalhes(usuario);
    modalUtils.abrirModal('modal_detalhes_usuario');
  };

  // Função para abrir modal de alterar perfil
  const handleAbrirAlterarPerfil = (usuario: Usuario) => {
    setUsuarioAlterarPerfil(usuario);
    modalUtils.abrirModal('modal_alterar_perfil');
  };

  // Função para abrir modal de exceções
  const handleAbrirExcecoes = (usuario: Usuario, initialTab: 'lista' | 'adicionar' = 'lista') => {
    setUsuarioExcecoes(usuario);
    setExcecoesInitialTab(initialTab);
    modalUtils.abrirModal('modal_excecoes_usuario');
  };

  // Função para abrir modal de adicionar usuário
  const handleAbrirAdicionarUsuario = () => {
    setShowModalAdicionarUsuario(true);
  };

  // Função para abrir modal de editar usuário
  const handleAbrirEditarUsuario = (usuario: Usuario) => {
    setUsuarioEditar(usuario);
  };

  // Função para abrir modal de deletar/reativar usuário
  const handleDeletarReativarUsuario = (usuario: Usuario, action: 'delete' | 'reactivate') => {
    setUsuarioDeletarReativar({usuario, action});
  };

  // Função para fechar modais
  const handleFecharModais = () => {
    setShowModalAdicionarUsuario(false);
    setUsuarioEditar(null);
    setUsuarioDeletarReativar(null);
  };

  // Função para obter badge do perfil
  const getPerfilBadge = (usuario: Usuario) => {
    if (!usuario.id_perfil_base || !usuario.perfil_nome) {
      return <span className="badge bg-warning text-dark">🚫 Sem Perfil</span>;
    }

    const cor = usuario.perfil_cor || '#6B7280';
    return (
      <span 
        className="badge" 
        style={{ 
          backgroundColor: cor,
          color: '#fff'
        }}
      >
        {usuario.perfil_nome}
      </span>
    );
  };

  // Função para obter status do usuário
  const getStatusBadge = (ativo: boolean) => {
    return ativo ? (
      <span className="badge badge-pill badge-status bg-success">Ativo</span>
    ) : (
      <span className="badge badge-pill badge-status bg-danger">Inativo</span>
    );
  };

  const columns: TableColumn[] = [
    {
      title: "Usuário",
      dataIndex: "nome",
      key: "nome",
      sorter: (a, b) => a.nome.localeCompare(b.nome),
      render: (_, record) => (
        <div className="d-flex align-items-center">
          <div className="avatar avatar-sm me-2">
            {record.foto_perfil_url ? (
              <img 
                src={BASE_DOMAIN + record.foto_perfil_url} 
                alt={record.nome}
                className="avatar-img rounded-circle"
              />
            ) : (
              <div 
                className="avatar-placeholder bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{ 
                  width: '32px',
                  height: '32px'
                }}
              >
                {record.nome.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div>
            <h6 className="mb-0">{record.nome} {record.sobrenome || ''}</h6>
            <small className="text-muted">{record.email}</small>
          </div>
        </div>
      ),
    },
    {
      title: "Perfil",
      dataIndex: "perfil_nome",
      key: "perfil",
      render: (_, record) => getPerfilBadge(record),
      sorter: (a, b) => {
        const aValue = a.perfil_nome || 'ZZZ_Sem perfil';
        const bValue = b.perfil_nome || 'ZZZ_Sem perfil';
        return aValue.localeCompare(bValue);
      },
    },
    {
      title: "Cargo",
      dataIndex: "cargo",
      key: "cargo",
      render: (cargo) => cargo || <span className="text-muted">-</span>,
      sorter: (a, b) => (a.cargo || '').localeCompare(b.cargo || ''),
    },
    {
      title: "CPF",
      dataIndex: "cpf",
      key: "cpf",
      render: (cpf) => cpf || <span className="text-muted">-</span>,
      sorter: (a, b) => (a.cpf || '').localeCompare(b.cpf || ''),
    },
    {
      title: "Telefone",
      dataIndex: "telefone_celular",
      key: "telefone",
      render: (telefone) => telefone || <span className="text-muted">-</span>,
      sorter: (a, b) => (a.telefone_celular || '').localeCompare(b.telefone_celular || ''),
    },
    {
      title: "Data Criação",
      dataIndex: "data_criacao",
      key: "data_criacao",
      render: (data_criacao) => {
        if (!data_criacao) return <span className="text-muted">-</span>;
        const date = new Date(data_criacao);
        return <span className="text-muted">{date.toLocaleDateString('pt-BR')}</span>;
      },
      sorter: (a, b) => {
        const aDate = a.data_criacao ? new Date(a.data_criacao).getTime() : 0;
        const bDate = b.data_criacao ? new Date(b.data_criacao).getTime() : 0;
        return bDate - aDate;
      },
    },
    {
      title: "Status",
      dataIndex: "ativo",
      key: "ativo",
      render: (ativo) => getStatusBadge(ativo),
      sorter: (a, b) => {
        const aValue = a.ativo ? 1 : 0;
        const bValue = b.ativo ? 1 : 0;
        return bValue - aValue; // Ativos primeiro
      },
    },
    {
      title: "Ações",
      key: "acoes",
      render: (_, record) => (
        <div className="d-flex gap-1">
          <Link
            to="#"
            className="btn btn-sm btn-outline-info"
            onClick={() => handleAbrirDetalhes(record)}
            title="Ver Detalhes"
          >
            <i className="ti ti-eye"></i>
          </Link>
          <Link
            to="#"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => handleAbrirEditarUsuario(record)}
            title="Editar Usuário"
          >
            <i className="ti ti-edit"></i>
          </Link>
          <Link
            to="#"
            className="btn btn-sm btn-outline-primary"
            onClick={() => handleAbrirAlterarPerfil(record)}
            title="Alterar Perfil"
          >
            <i className="ti ti-user-cog"></i>
          </Link>
          <Link
            to="#"
            className="btn btn-sm btn-outline-warning"
            onClick={() => handleAbrirExcecoes(record)}
            title="Gerenciar Exceções"
          >
            <i className="ti ti-alert-triangle"></i>
          </Link>
          {record.ativo ? (
            <Link
              to="#"
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDeletarReativarUsuario(record, 'delete')}
              title="Desativar Usuário"
            >
              <i className="ti ti-user-x"></i>
            </Link>
          ) : (
            <Link
              to="#"
              className="btn btn-sm btn-outline-success"
              onClick={() => handleDeletarReativarUsuario(record, 'reactivate')}
              title="Reativar Usuário"
            >
              <i className="ti ti-user-check"></i>
            </Link>
          )}
        </div>
      ),
    }
  ];

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Contadores para estatísticas
  const totalUsuarios = usuarios.length;
  const usuariosAtivos = usuarios.filter(u => u.ativo).length;
  const usuariosSemPerfil = usuarios.filter(u => !u.id_perfil_base).length;

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      👥 Gestão de Usuários<span className="count-title">{filteredUsuarios.length} usuários</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cards de Estatísticas */}
              <div className="row mb-4">
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-primary-light rounded me-3">
                          <i className="ti ti-users fs-24 text-primary"></i>
                        </div>
                        <div>
                          <span className="text-muted">Total de Usuários</span>
                          <h4 className="mb-0">{totalUsuarios}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-success-light rounded me-3">
                          <i className="ti ti-user-check fs-24 text-success"></i>
                        </div>
                        <div>
                          <span className="text-muted">Usuários Ativos</span>
                          <h4 className="mb-0">{usuariosAtivos}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-warning-light rounded me-3">
                          <i className="ti ti-user-exclamation fs-24 text-warning"></i>
                        </div>
                        <div>
                          <span className="text-muted">Sem Perfil</span>
                          <h4 className="mb-0">{usuariosSemPerfil}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-xl-3 col-md-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="avatar avatar-md bg-info-light rounded me-3">
                          <i className="ti ti-shield-check fs-24 text-info"></i>
                        </div>
                        <div>
                          <span className="text-muted">Total de Perfis</span>
                          <h4 className="mb-0">{perfis.length}</h4>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Usuário"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-4">
                      <div className="d-flex align-items-center">
                        <label className="me-2 text-muted">Filtrar por perfil:</label>
                        <select 
                          className="form-select form-select-sm"
                          value={perfilFilter}
                          onChange={(e) => setPerfilFilter(e.target.value)}
                        >
                          <option value="all">Todos os perfis</option>
                          <option value="sem-perfil">⚠️ Sem perfil</option>
                          {perfis.map(perfil => (
                            <option key={perfil.id} value={perfil.codigo}>
                              {perfil.nome}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Último Login
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Por Perfil
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botão Adicionar e Alerta para usuários sem perfil */}
                    <div className="d-flex align-items-center gap-3">
                      <button
                        className="btn btn-primary"
                        onClick={handleAbrirAdicionarUsuario}
                      >
                        <i className="ti ti-plus me-2"></i>
                        Adicionar Usuário
                      </button>
                      
                      {usuariosSemPerfil > 0 && (
                        <div className="alert alert-warning mb-0 py-2 px-3">
                          <i className="ti ti-alert-triangle me-1"></i>
                          <strong>{usuariosSemPerfil}</strong> usuário(s) sem perfil definido
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tabela de Usuários */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredUsuarios.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum usuário encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredUsuarios.map(usuario => ({
                          ...usuario,
                          key: usuario.id.toString()
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Detalhes do Usuário */}
      {usuarioDetalhes && (
        <ModalDetalhesUsuario
          usuario={usuarioDetalhes}
          onClose={() => {
            setUsuarioDetalhes(null);
            modalUtils.fecharModal('modal_detalhes_usuario');
          }}
          onRefresh={fetchUsuarios}
          onAbrirExcecoes={(usuario) => handleAbrirExcecoes(usuario, 'adicionar')}
        />
      )}

      {/* Modal Alterar Perfil */}
      {usuarioAlterarPerfil && (
        <ModalAlterarPerfil
          usuario={usuarioAlterarPerfil}
          onClose={() => {
            setUsuarioAlterarPerfil(null);
            modalUtils.fecharModal('modal_alterar_perfil');
          }}
          onRefresh={fetchUsuarios}
        />
      )}

      {/* Modal Exceções do Usuário */}
      {usuarioExcecoes && (
        <ModalExcecoesUsuario
          usuario={usuarioExcecoes}
          onClose={() => {
            setUsuarioExcecoes(null);
            modalUtils.fecharModal('modal_excecoes_usuario');
          }}
          onRefresh={fetchUsuarios}
          initialTab={excecoesInitialTab}
        />
      )}

      {/* Modal Adicionar Usuário */}
      {showModalAdicionarUsuario && (
        <ModalAdicionarUsuario
          onClose={() => {
            setShowModalAdicionarUsuario(false);
          }}
          onRefresh={fetchUsuarios}
        />
      )}

      {/* Modal Editar Usuário */}
      {usuarioEditar && (
        <ModalEditarUsuario
          usuario={usuarioEditar}
          onClose={() => {
            setUsuarioEditar(null);
          }}
          onRefresh={fetchUsuarios}
        />
      )}

      {/* Modal Deletar/Reativar Usuário */}
      {usuarioDeletarReativar && (
        <ModalDeletarReativarUsuario
          usuario={usuarioDeletarReativar.usuario}
          action={usuarioDeletarReativar.action}
          onClose={() => {
            setUsuarioDeletarReativar(null);
          }}
          onRefresh={fetchUsuarios}
        />
      )}
    </>
  );
};